import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ListChecks } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTABlock } from "@/components/CTABlock";
import { ARTICLES } from "@/data/articles";
import { ARTICLES_EN } from "@/data/articles.en";
import { useLang } from "@/i18n/LangContext";

const LinkifiedText = ({ text }) => {
  const parts = text.split(/(\[\[[^\]|]+\|[^\]]+\]\])/g);
  return parts.map((part, i) => {
    const match = part.match(/^\[\[([^\]|]+)\|([^\]]+)\]\]$/);
    if (!match) return <BoldText key={i} text={part} />;
    return (
      <Link
        key={i}
        to={match[1]}
        className="font-semibold text-terracotta-deep underline decoration-terracotta/40 underline-offset-4 hover:decoration-terracotta"
      >
        {match[2]}
      </Link>
    );
  });
};

const BoldText = ({ text }) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") ? (
      <strong key={i} className="font-semibold text-ink">{part.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return <h2>{block.text}</h2>;
    case "h3":
      return <h3>{block.text}</h3>;
    case "list":
      return (
        <ul>
          {block.items.map((item) => (
            <li key={item}><LinkifiedText text={item} /></li>
          ))}
        </ul>
      );
    case "table":
      return (
        <div className="overflow-x-auto mb-8 rounded-2xl">
          <table>
            <thead>
              <tr>
                {block.headers.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return <p><LinkifiedText text={block.text} /></p>;
  }
};

export default function Article() {
  const { slug } = useParams();
  const { t, l, lang } = useLang();
  const articles = lang === "en" ? ARTICLES_EN : ARTICLES;
  const article = articles.find((a) => a.slug === slug);
  const ap = t.articlePage;
  const formatDate = (d) =>
    new Date(d).toLocaleDateString(t.meta.dateLocale, { day: "numeric", month: "long", year: "numeric" });
  if (!article) return <Navigate to={l("/blog")} replace />;
  const related = articles.filter((a) => a.slug !== slug).slice(0, 2);
  const frSlug = article.frSlug || article.slug;

  return (
    <>
      <Seo
        title={article.seo.title}
        description={article.seo.description}
        path={`/blog/${frSlug}`}
        type="article"
        schemas={[
          breadcrumbSchema([
            { name: t.common.home, path: "/" },
            { name: ap.breadcrumbBlog, path: "/blog" },
            { name: article.title, path: `/blog/${frSlug}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.seo.description,
            datePublished: article.date,
            inLanguage: lang === "en" ? "en" : "fr",
            author: { "@type": "Person", name: "Magali Clouzet", jobTitle: t.role },
            articleSection: article.category,
          },
        ]}
      />

      <article className="px-5 md:px-10 pt-36 md:pt-44 pb-16" data-testid="article">
        <div className="mx-auto max-w-3xl">
          <Breadcrumb
            items={[
              { label: t.common.home, to: "/" },
              { label: ap.breadcrumbBlog, to: "/blog" },
              { label: article.category },
            ]}
          />
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-terracotta-deep">
              {article.category}
            </span>
            <h1 className="mt-4 font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-balance">
              {article.title}
            </h1>
            <p className="mt-5 text-sm text-plum/60">
              {ap.by} <strong className="text-ink">Magali Clouzet</strong> · {formatDate(article.date)} ·{" "}
              {article.readTime} {t.common.readSuffix}
            </p>
          </Reveal>

          <Reveal delay={0.15} className="mt-10">
            <div className="overflow-hidden rounded-[2rem]">
              <img src={article.image} alt={article.imageAlt} className="h-64 md:h-96 w-full object-cover" />
            </div>
          </Reveal>

          <Reveal delay={0.2} className="mt-10">
            <div className="rounded-3xl border-l-4 border-terracotta bg-white p-7 md:p-9">
              <h2 className="flex items-center gap-2.5 font-display text-base font-bold tracking-tight text-terracotta-deep">
                <ListChecks className="w-5 h-5" aria-hidden="true" /> {ap.summaryTitle}
              </h2>
              <ul className="mt-4 space-y-2.5">
                {article.summary.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-sm md:text-base text-plum/90">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-terracotta" aria-hidden="true" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="article-body mt-4">
            <div>
              {article.content.map((block, i) => (
                <Block key={i} block={block} />
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-14">
            <div className="flex items-center gap-5 rounded-3xl bg-sand/60 p-7">
              <img
                src="/assets/magali-clouzet-consultante-marketing-pme-antibes-cannes.png"
                alt={ap.authorAlt}
                className="h-14 w-14 shrink-0 rounded-full object-cover"
              />
              <div>
                <p className="font-display font-bold tracking-tight">Magali Clouzet</p>
                <p className="mt-1 text-sm leading-relaxed text-plum/75">
                  {ap.authorBio}{" "}
                  <Link to={l("/qui-suis-je")} data-testid="article-author-link" className="font-semibold text-terracotta-deep underline decoration-terracotta/40 underline-offset-4">
                    {ap.authorLink}
                  </Link>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </article>

      <section className="px-5 md:px-10 pb-20" data-testid="article-related">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold tracking-tight">{ap.relatedTitle}</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={l(`/blog/${a.frSlug || a.slug}`)}
                data-testid={`article-related-${a.slug}`}
                className="group rounded-3xl border border-ink/5 bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_-15px_rgba(17,14,13,0.12)]"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-terracotta-deep">
                  {a.category}
                </span>
                <p className="mt-2 font-display font-bold tracking-tight leading-snug">{a.title}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-terracotta-deep">
                  {ap.readArticle}
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
          <Link
            to={l("/blog")}
            data-testid="article-back"
            className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-plum/70 hover:text-terracotta-deep transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" /> {ap.backToBlog}
          </Link>
        </div>
      </section>

      <CTABlock ctaLabel={article.ctaLabel} />
    </>
  );
}
